import React, { useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { View, Image } from 'react-native';
import { launchImageLibrary } from 'utils/packages/imagePicker';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import Button from 'design-system/Button';
import { rem } from 'design-system/values';
import { AppView, ButtonGroup, Indicator, StatusBar } from 'design-system';
import useStyles from 'hooks/useStyles';
import useColors from 'hooks/useColors';
import Amplitude from 'utils/amplitude';
import { useAppDispatch } from 'store/hooks';
import createStyleSheets from 'utils/createStyleSheets';
import { OnboardingFlow } from 'utils/types/navigation';
import { getUserAddresses, postUserAddress } from 'actions';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useHeaderHeight from 'hooks/useHeaderHeight';
import { LoadingAppViewModal } from 'design-system/LoadingAppView';
import { getOnboardingFlow } from '../utils/analytics';
import useDocumentUpload from '../hooks/useDocumentUpload';
import { VIEW_FINDER_TOP, VIEW_FINDER_HEIGHT } from '../constants';
import { isWeb } from '../../../constants';
import { useNavigateAfterPOAUpload } from '../hooks/useNavigateAfterPOAUpload';
import EnlargeDocumentButton from '../components/EnlargeDocumentButton';
import { getTradingAccounts } from '../actions';
const strings = {
    navTitle: 'Proof of Address',
    uploading: 'Uploading...',
};
const PREVIEW_HORIZONTAL_PADDING = rem(16);
const PREVIEW_BORDER_RADIUS = 13; // no rem, because corners are SVG paths that have fixed shape which should match this corner radius
const ProofOfAddressPreviewScreen = ({ route, navigation }) => {
    const { width: WIDTH, paddingHorizontal } = useAppFrameDimensions(isWeb);
    const colors = useColors();
    const styles = useStyles(styleSet);
    const headerHeight = useHeaderHeight();
    const { PREVIEW_MIN_TOP, TEXTS_TOP, PREVIEW_MAX_HEIGHT } = useMemo(() => {
        const PREVIEW_MIN_TOP = headerHeight + rem(20); // can go lower, but not higher
        const TEXTS_TOP = VIEW_FINDER_TOP + VIEW_FINDER_HEIGHT + rem(80);
        const PREVIEW_MAX_HEIGHT = TEXTS_TOP - PREVIEW_MIN_TOP - rem(60);
        return { PREVIEW_MIN_TOP, TEXTS_TOP, PREVIEW_MAX_HEIGHT };
    }, [headerHeight]);
    const flow = route.params?.flow;
    const address = route.params?.address;
    const isFromCamera = route.params?.isFromCamera;
    const isAddressFlow = flow === OnboardingFlow.Address;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    const dispatch = useAppDispatch();
    const navigateAfterUpload = useNavigateAfterPOAUpload(flow);
    const [asset, setAsset] = useState({
        uri: route.params?.uri,
        type: route.params?.type,
        width: route.params?.width,
        height: route.params?.height,
    });
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [upload] = useDocumentUpload(flow);
    const onOpenLibrary = useCallback(async () => {
        if (isFromCamera) {
            navigation.goBack();
            return;
        }
        const timeout = setTimeout(() => {
            setLoadingPreview(true);
        }, 300);
        try {
            const { assets } = await new Promise((resolve, reject) => {
                launchImageLibrary({
                    mediaType: 'photo',
                }, (response) => {
                    if (response.didCancel) {
                        reject(new Error());
                    }
                    else if (response.assets) {
                        resolve(response);
                    }
                    else {
                        reject(new Error());
                    }
                });
            });
            clearTimeout(timeout);
            if (assets && assets.length) {
                const { uri, type, width, height } = assets[0];
                if (uri && width && height) {
                    setAsset({ uri, type, width, height });
                }
            }
            setLoadingPreview(false);
        }
        catch {
            // user cancelled, nothing to do
            clearTimeout(timeout);
            setLoadingPreview(false);
        }
    }, [isFromCamera]);
    const onUpload = useCallback(async () => {
        if (isAddressFlow && !address) {
            return;
        }
        LoadingAppViewModal.show({ title: strings.uploading });
        try {
            let newAddress;
            if (isAddressFlow && address) {
                const addressId = address?.id;
                /**
                 * means that the address has not been posted yet
                 * so we need to post the address
                 */
                if (!addressId) {
                    const postAddressResponse = await dispatch(postUserAddress(address));
                    if (postAddressResponse?.payload?.address) {
                        newAddress = postAddressResponse.payload.address;
                    }
                    else {
                        return;
                    }
                }
            }
            const addressToUse = newAddress ?? address;
            await upload({
                files: [{ path: asset.uri, width: asset.width, height: asset.height }],
                type: 'ADDRESS',
                mimeType: asset.type,
                addressId: addressToUse?.id,
            });
            if (addressToUse) {
                dispatch({
                    type: 'SET_NEW_ADDRESS_IN_USER_ADDRESSES',
                    address: { ...newAddress, kycStatus: 'PENDING', kycSubmittedAt: new Date().toISOString() },
                });
            }
            Amplitude.logEvent('Submitted Request', {
                source: getOnboardingFlow(flow),
                type: 'kyc_poa',
            });
            Amplitude.logEvent('Submitted Request', {
                source: getOnboardingFlow(flow),
                type: 'kyc_verification',
            });
            // reset document type in invest store
            dispatch({
                type: 'SET_KYC_DOCUMENT_TYPE',
                documentType: undefined,
            });
            if (isAddressFlow) {
                await dispatch(getUserAddresses());
            }
            else {
                try {
                    await dispatch(getTradingAccounts());
                }
                catch {
                    //
                }
            }
            LoadingAppViewModal.show({ isDone: true });
            navigateAfterUpload();
            LoadingAppViewModal.hide();
        }
        catch (error) {
            LoadingAppViewModal.hide(() => {
                Modal.showError(error.message);
            });
        }
    }, [isAddressFlow, address, upload, asset.uri, asset.width, asset.height, asset.type, flow, navigateAfterUpload]);
    const imagePreview = useMemo(() => {
        const { uri, width, height } = asset;
        const previewContainerWidth = WIDTH - PREVIEW_HORIZONTAL_PADDING * 2;
        const previewContainerHeight = PREVIEW_MAX_HEIGHT;
        const widthRatio = width / previewContainerWidth;
        const heightRatio = height / previewContainerHeight;
        let imageWidth = 0;
        let imageHeight = 0;
        if (widthRatio > heightRatio) {
            // need to scale down based on height, because height fits, but width does not
            imageWidth = previewContainerWidth; // width / widthRatio
            imageHeight = height / widthRatio;
        }
        else {
            imageWidth = width / heightRatio;
            imageHeight = previewContainerHeight;
        }
        return (<View style={[
                styles.previewContainer,
                {
                    top: PREVIEW_MIN_TOP,
                    height: PREVIEW_MAX_HEIGHT,
                },
            ]}>
        {loadingPreview ? (<Indicator style={styles.loadingIndicator} color={colors.text.whiteOnColor}/>) : (<Image style={[styles.preview, { width: imageWidth, height: imageHeight }]} source={{ uri }}/>)}
        <View style={styles.enlargeBtnContainer}>
          <EnlargeDocumentButton source={{ uri }}/>
        </View>
      </View>);
    }, [
        asset,
        WIDTH,
        PREVIEW_MAX_HEIGHT,
        styles.previewContainer,
        styles.loadingIndicator,
        styles.preview,
        styles.enlargeBtnContainer,
        PREVIEW_MIN_TOP,
        loadingPreview,
        colors.text.whiteOnColor,
    ]);
    return (<AppView>
      {imagePreview}
      <StatusBar barStyle="dark-content" animated/>
      <View style={[
            styles.textsContainer,
            styles.textsContainerPoa,
            { paddingHorizontal: (paddingHorizontal || 0) + PREVIEW_HORIZONTAL_PADDING, top: TEXTS_TOP },
        ]} pointerEvents="none">
        <Text Text-16 centered>
          Make sure you have uploaded the entire document page and the details are clear to read with no blur or glare
        </Text>
      </View>
      <ButtonGroup type="floating">
        <Button brandReversed title={isFromCamera ? 'Retake' : 'Choose another photo'} onPress={onOpenLibrary}/>
        <Button brand preventDoubleTap title="Submit document" onPress={onUpload}/>
      </ButtonGroup>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    previewContainer: {
        position: 'absolute',
        left: PREVIEW_HORIZONTAL_PADDING,
        right: PREVIEW_HORIZONTAL_PADDING,
        alignItems: 'center',
        justifyContent: 'center',
    },
    preview: {
        borderRadius: PREVIEW_BORDER_RADIUS,
    },
    textsContainer: {
        position: 'absolute',
        left: 0,
        right: 0,
        padding: PREVIEW_HORIZONTAL_PADDING,
    },
    textsContainerPoa: {
        marginTop: rem(-40),
    },
    loadingIndicator: {
        backgroundColor: 'transparent',
    },
    enlargeBtnContainer: {
        left: 0,
        right: 0,
        bottom: rem(8),
        position: 'absolute',
        alignItems: 'center',
    },
}));
export default ProofOfAddressPreviewScreen;
